import { capitalize, intersection } from 'lodash';
import { keyBy } from 'lodash/fp';
import { useCallback, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import CaseActionModalV2 from 'dashboard/components/CaseActionModal/CaseActionModalV2';
import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned/Permissioned';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import FormAssignCase, { AssignCaseFormData } from 'dashboard/forms/FormAssignCase';
import FormSetCaseState from 'dashboard/forms/FormSetCaseState';
import { FormSetCaseStateData } from 'dashboard/forms/FormSetCaseState/FormSetCaseState';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { NextPageSetting } from 'dashboard/lib/constants/case';
import { printMixins } from 'dashboard/lib/printMixins';
import { Account } from 'dashboard/models/account';
import { Case, CaseActionWorkflow, CaseDataManager } from 'dashboard/models/case';
import { CaseState, CaseTemplate, StateTag } from 'dashboard/models/case-template';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import IconCaretDown from 'images/icons/caret-down-24x24.svg';
import IconMore from 'images/icons/more.svg';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';
import { captureException } from 'lib/reporting';
import { Json } from 'lib/types';
import { convertKeysToSnakeCase, runJsonLogic } from 'lib/utils/jsonLogicRunner';

const Container = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;

  ${printMixins.hide}
`;

const Header = styled(NavBack)`
  justify-self: start;
`;

interface CaseHeaderProps {
  case: Case;
  caseDataManager: CaseDataManager;
  caseTemplate: CaseTemplate;
  currentEnvironment: Environment;
  actionWorkflows: CaseActionWorkflow[];
  accounts: Account[];
  inquiries: Inquiry[];
  templates: (InquiryTemplate | Template)[];
  tags: Tag[];
  verifications: Verification[];
  users: User[];
  nextPageSettingDefault: NextPageSetting;
  assignCase: (values: AssignCaseFormData) => Promise<void>;
  handleSetCaseState: (stateId: string, values: FormSetCaseStateData) => Promise<void>;
  handleRunCaseActionWorkflow: (values: Json, workflowId: string) => Promise<void>;
  handleRedactCase: (id: string) => Promise<void>;
}

const CaseHeader = ({
  case: kase,
  caseTemplate,
  caseDataManager,
  currentEnvironment,
  users,
  accounts,
  inquiries,
  tags,
  verifications,
  actionWorkflows,
  nextPageSettingDefault,
  assignCase,
  handleSetCaseState,
  handleRunCaseActionWorkflow,
  handleRedactCase,
}: CaseHeaderProps) => {
  const { url } = useRouteMatch();

  const [assignModal, { enable: showAssignModal, disable: hideAssignModal }] = useToggle(false);
  const [workflowModal, { enable: showWorkflowModal, disable: hideWorkflowModal }] =
    useToggle(false);
  const [setStateModal, { enable: showSetStateModal, disable: hideSetStateModal }] =
    useToggle(false);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [selectedState, setSelectedState] = useState<CaseState>(undefined);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [selectedStateTag, setSelectedStateTag] = useState<StateTag>(undefined);
  const [selectedCaseActionWorkflow, setSelectedCaseActionWorkflow] =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    useState<CaseActionWorkflow>(undefined);
  const [showRedactCaseModal, { prompt, confirm, cancel }] = useConfirmation();

  const isRedacted = kase.attributes['redacted-at'] != null;
  const actionWorkflowsMap = keyBy((x) => x.workflow.id, actionWorkflows);
  const caseDataManagerWithSnakeCase = convertKeysToSnakeCase(caseDataManager);

  const handleAssignCaseAndClose = useCallback(
    async (values: AssignCaseFormData) => {
      await assignCase(values);
      hideAssignModal();
    },
    [assignCase],
  );

  const setCaseStateAndClose = async ({ tags, message, nextPageSetting }: FormSetCaseStateData) => {
    await handleSetCaseState(selectedState.token, {
      tags: tags,
      message: message,
      nextPageSetting: nextPageSetting,
    });
    hideSetStateModal();
  };

  const handleRedactCaseModal = () => prompt(() => handleRedactCase(kase.id));

  const customStatusItems = (kase.attributes.state?.['next-state-ids'] || [])
    .map((id: string) => {
      // Added to help debug https://persona-identities-inc.sentry.io/issues/3799533360
      // We can delete after we figure this out.
      try {
        const state = caseTemplate.attributes.configuration.states.find(
          (state) => state.token === id,
        );
        const stateTag = caseTemplate.attributes.configuration['state-tags'].find(
          (stateTag) =>
            stateTag['state-token'] === id &&
            stateTag['environment-type'] === currentEnvironment.type,
        );
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const { name, token } = state;
        const availableTags = stateTag?.['available-tags'];
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const toTransitionRules = state['to-transition-rules'];
        const stateShouldBeDisabled = !toTransitionRules.every((rule) =>
          // NB: Since transition rules are defined to be run on the backend, we need to convert the data manager to snake case to match the keys on the backend.
          runJsonLogic(rule.configuration, caseDataManagerWithSnakeCase),
        );

        const onClick = () => {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          setSelectedState(state);
          if (availableTags && availableTags.length > 0) {
            setSelectedStateTag(stateTag);
          }
          showSetStateModal();
        };
        return {
          id: `case-header-status__${token}`,
          'data-test': `case-header-status__${token}`,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          component: <CaseStatusSticker disabled={stateShouldBeDisabled} caseState={state} />,
          label: capitalize(name),
          onClick: onClick,
          permission: Permission.CaseWrite,
          disabled: stateShouldBeDisabled,
        };
      } catch (e) {
        captureException(e, 'error', {
          nextStateId: id,
          caseTemplateStates: JSON.stringify(caseTemplate.attributes.configuration.states),
        });
        console.error(e);
        return null;
      }
    })
    .filter(Boolean);

  // temporary solution to not show case action from other environment
  const actionWorkflowIdsInCurrentEvironment = actionWorkflows.map((action) => action.workflow.id);
  const caseActionIds = intersection(
    caseTemplate.attributes.configuration['case-action-ids'],
    actionWorkflowIdsInCurrentEvironment,
  );

  const caseActionItems = caseActionIds.map((id) => {
    const actionWorkflow = actionWorkflowsMap[id];
    const { workflow, trigger } = actionWorkflow;
    //if no input is expected, run action directly without opening a modal
    const onClick = () => {
      if (trigger.attributes['param-schema'].length === 0) {
        handleRunCaseActionWorkflow({}, workflow.id);
      } else {
        setSelectedCaseActionWorkflow(actionWorkflow);
        showWorkflowModal();
      }
    };

    return {
      id: `case-header-action__${workflow.id}`,
      name: `case-header-action__${workflow.id}`,
      'data-test': `case-header-action__${workflow.id}`,
      onClick: onClick,
      variant: 'basic' as const,
      label: workflow.attributes.name,
    };
  });

  const moreItems = [
    {
      id: 'case-header-action__print-case',
      name: 'case-header-action__print-case',
      onClick: () => {
        window.open(`${url}/print`, '_blank', 'noopener,noreferrer');
      },
      label: 'Print case',
      hidden: !usePermission(Permission.CasesExport),
    },
    {
      id: 'case-header-action__redact-case',
      name: 'case-header-action__redact-case',
      'data-test': 'case-header-action__redact-case',
      onClick: handleRedactCaseModal,
      label: 'Redact case',
      hidden: !usePermission(Permission.CaseRedact) || isRedacted,
      variant: 'danger' as const,
    },
    ...caseActionItems,
  ].filter((item) => ('hidden' in item ? !item.hidden : true));

  const assigneeId = kase.relationships.assignee.data?.id;
  const hasCaseWritePermission = usePermission(Permission.CaseWrite);

  return (
    <>
      <Container>
        <Header to='/dashboard/cases'>All Cases</Header>
        <HStack css={{ justifySelf: 'end' }}>
          <DropdownMenu
            trigger={
              <Button
                id='case-header-status__button--dropdown'
                disabled={!hasCaseWritePermission}
                tabIndex={0}
                data-test='case-header-status__button--dropdown'
                variant='basic'
                width='auto'
                suffixIcon={hasCaseWritePermission && <IconCaretDown />}
              >
                Status: {capitalize(kase.attributes.state.name)}
              </Button>
            }
            align='center'
          >
            {customStatusItems.map((item) => (
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              <Permissioned permission={item.permission} key={item.id}>
                <DropdownMenu.Item
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  data-test={item['data-test']}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  id={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    item.onClick();
                  }}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  key={item.id}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  disabled={item.disabled}
                >
                  {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                  {item.component}
                </DropdownMenu.Item>
              </Permissioned>
            ))}
          </DropdownMenu>
          <Permissioned permission={Permission.CaseAssign}>
            <Button id='case-header__button--assign' variant='basic' onClick={showAssignModal}>
              Assign
            </Button>
          </Permissioned>

          <DropdownMenu
            trigger={
              <Button
                id='case-header-action__button--dropdown'
                tabIndex={0}
                data-test='case-header-action__button--dropdown'
                variant='basic'
                width='auto'
                prefixIcon={<IconMore />}
              >
                More
              </Button>
            }
            align='end'
          >
            {moreItems.map((item) => (
              <DropdownMenu.Item
                data-test={item['data-test']}
                id={item.id}
                variant={item.variant}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  item.onClick();
                }}
                key={item.id}
              >
                {item.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu>
        </HStack>
      </Container>

      <ModalWrapper isOpen={assignModal} handleCloseModal={hideAssignModal}>
        <ModalContainer data-test='assign-case-modal'>
          <ModalHeader onClose={hideAssignModal}>Assign case</ModalHeader>
          <ModalBody>
            <FormAssignCase
              initialValues={{ userId: assigneeId }}
              handleCancel={hideAssignModal}
              onSubmit={handleAssignCaseAndClose}
              users={users}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>

      <ModalWrapper isOpen={showRedactCaseModal} handleCloseModal={cancel}>
        <ConfirmationModal
          cancelAction={cancel}
          confirmAction={confirm}
          header='Redact this case’s PII?'
          confirmActionText='Redact'
        >
          Personally identifiable information will be redacted. This action is permanent and can’t
          be undone.
        </ConfirmationModal>
      </ModalWrapper>

      <ModalWrapper isOpen={setStateModal} handleCloseModal={hideSetStateModal}>
        <ModalContainer data-test='set-case-state-modal'>
          <ModalHeader onClose={hideSetStateModal}> Update Case </ModalHeader>
          <ModalBody>
            <FormSetCaseState
              defaultValues={{ nextPageSetting: nextPageSettingDefault }}
              state={selectedState}
              stateTag={selectedStateTag}
              tags={tags}
              handleCancel={hideSetStateModal}
              setCaseStateAndClose={setCaseStateAndClose}
              caseCommentTemplates={caseTemplate.attributes.configuration['case-comment-templates']}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>

      <ModalWrapper isOpen={workflowModal} handleCloseModal={hideWorkflowModal}>
        <CaseActionModalV2
          case={kase}
          accounts={accounts}
          inquiries={inquiries}
          verifications={verifications}
          caseDataManager={caseDataManager}
          caseAction={selectedCaseActionWorkflow}
          handleRunWorkflow={handleRunCaseActionWorkflow}
          hideModal={hideWorkflowModal}
        />
      </ModalWrapper>
    </>
  );
};

export default CaseHeader;
